import { ReactElement, useEffect, useRef, useState } from 'react';

import {
  FieldControlApi,
  FormField,
  InputApi,
  InputTypes,
  IntegrationThemes,
  PayLoadValue,
  ValidationStatuses,
} from '@cashier/cashier-model';
import { Select } from '@cashier/cashier-ui';

import { useFieldRegistryContext } from '../../../contexts';
import { useCashierConfiguration, useFormPayloadItem } from '../../../hooks';
import { useAmountField } from '../hooks';
import { Input } from '../modules/input/v3';

interface AmountFieldProps {
  field: FormField;
}

export const AmountField = ({ field }: AmountFieldProps): ReactElement => {
  const inputRef = useRef<InputApi>(null);
  const apiRef = useRef<FieldControlApi<PayLoadValue> | null>(null);

  const [isBalanceInitiated, setIsBalanceInitiated] = useState<boolean>(false);

  const registry = useFieldRegistryContext();
  const { direction } = useCashierConfiguration();
  const { isDisabled, validationStatus } = useFormPayloadItem(field.name);

  const { onChange, balance } = useAmountField({ field });

  const { type, name, label, params, value } = field;

  apiRef.current ??= {
    getValue: () => inputRef.current?.getValue() ?? '',
    setValue: (value) => {
      inputRef.current?.setValue(String(value ?? ''));
    },
  };

  useEffect(() => {
    if (registry && apiRef.current) {
      return registry.registerControl(field.name, apiRef.current);
    }
  }, [field.name, registry]);

  useEffect(() => {
    if (isBalanceInitiated) {
      inputRef.current && onChange(inputRef.current.getValue(), false);
    } else {
      setIsBalanceInitiated(true);
    }
  }, [balance]);

  return (
    <>
      {type !== InputTypes.Select && (
        <Input
          ref={inputRef}
          name={name}
          label={label || ''}
          type={type}
          value={value ? (value as string) : ''}
          min={params?.min || 0}
          max={params?.max || 10000}
          step={params?.step ?? null}
          scale={params?.scale}
          currency={params?.currency || null}
          isError={validationStatus === ValidationStatuses.FAILED}
          onChange={onChange}
          isDisabled={field.isDisabled || isDisabled}
          direction={direction}
        />
      )}
      {type === InputTypes.Select && (
        <Select
          ref={inputRef}
          name={name}
          direction={direction}
          label={label}
          options={params?.options || []}
          isError={validationStatus === ValidationStatuses.FAILED}
          onChange={(value) => onChange(value, false)}
          defaultValue={value ? (value as string) : ''}
          isDisabled={isDisabled}
          version={'v3'}
          theme={IntegrationThemes.Platform}
        />
      )}
    </>
  );
};
